/** A bounded native jewel finish, independent of rules and phase playback.
 * Visible settled boards receive six staggered facet glints and one charged halo at most.
 * Hidden, inert, disconnected and reduced-motion surfaces allocate no effects.
 * Call update after board paint/visibility changes; disposal releases all work. */
export function attachGemSparkle(board, { visible = () => true, readState = () => null } = {}) {
  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  const effects = new Set();
  let timer = 0;
  let disposed = false;
  let sequence = 0;

  function eligible() {
    return (
      !disposed &&
      board.isConnected &&
      !document.hidden &&
      !media.matches &&
      visible() &&
      !board.inert &&
      board.getAttribute('aria-busy') !== 'true' &&
      readState()?.gameId === 'gem-swap'
    );
  }
  function cancel() {
    clearTimeout(timer);
    timer = 0;
    for (const effect of effects) {
      effect.animation.cancel();
      effect.node.remove();
    }
    effects.clear();
  }
  function finish(cell, halo, offset, delay = 0) {
    const node = document.createElement('span');
    if (typeof node.animate !== 'function') return false;
    node.className = halo ? 'game-jewel-halo' : 'game-jewel-glint';
    node.setAttribute('aria-hidden', 'true');
    node.style.setProperty('--glint-x', `${25 + (offset % 3) * 13}%`);
    node.style.setProperty('--glint-y', `${20 + (offset % 2) * 10}%`);
    cell.append(node);
    const frames = halo
      ? [
          { opacity: 0, transform: 'scale(.85)' },
          { opacity: 0.58, transform: 'scale(1)', offset: 0.5 },
          { opacity: 0, transform: 'scale(1.08)' },
        ]
      : [
          { opacity: 0, transform: 'translate(-3px,2px) scale(.5)' },
          { opacity: 1, transform: 'translate(0,0) scale(1)', offset: 0.5 },
          { opacity: 0, transform: 'translate(4px,-2px) scale(.6)' },
        ];
    let animation;
    try {
      animation = node.animate(frames, {
        duration: halo ? 2400 : 1500,
        delay,
        easing: 'ease-in-out',
        fill: 'none',
      });
    } catch {
      node.remove();
      return false;
    }
    const effect = { node, animation };
    effects.add(effect);
    animation.onfinish = () => {
      node.remove();
      effects.delete(effect);
    };
    return true;
  }
  function sparkle() {
    timer = 0;
    if (!eligible()) return cancel();
    cancel();
    const cells = [...board.querySelectorAll('.game-cell')];
    if (!cells.length) return;
    // Traversal is presentation-only; it neither samples nor changes game state.
    sequence += 1;
    let started = false;
    for (let index = 0; index < Math.min(6, cells.length); index++) {
      const cell =
        cells[
          (sequence * 11 + Math.floor((index * cells.length) / Math.min(6, cells.length))) %
            cells.length
        ];
      started = finish(cell, false, sequence + index, index * 130) || started;
    }
    const charged = cells.filter((cell) => cell.dataset.power && cell.dataset.power !== 'ordinary');
    if (charged.length) finish(charged[sequence % charged.length], true, sequence);
    void started;
  }
  function update() {
    cancel();
    if (eligible()) timer = setTimeout(sparkle, 350);
  }
  function dispose() {
    if (disposed) return;
    disposed = true;
    cancel();
    document.removeEventListener('visibilitychange', update);
    media.removeEventListener?.('change', update);
  }
  document.addEventListener('visibilitychange', update);
  media.addEventListener?.('change', update);
  update();
  return { update, dispose };
}
